// Vendor-wise Items & Pricing (module: purchases_vendor_pricing, default off).
//
// Each vendor can carry a private price list. On Purchase Order, GRN, Purchase
// Invoice and Non-Tax Purchase Invoice, picking an item for that vendor fills
// the rate from the list (items not on the list keep their normal purchase
// price). Purchase Returns are not touched: their lines always come from the
// invoice, at the invoice price.
//
// Storage:
//   companies/{companyId}/vendorItemPricing/{vendorId}__{itemId}
//   { vendorId, itemId, itemCode, itemName, price, createdAt, updatedAt }
//
// Firebase cost: nothing is read while the module is off; when on, one small
// query per vendor picked on a purchase screen.

import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { conversionFactor } from './unitConversion';

export const VENDOR_PRICING_MODULE_ID = 'purchases_vendor_pricing';
export const VENDOR_PRICING_COLLECTION = 'vendorItemPricing';

export const EMPTY_VENDOR_PRICING = Object.freeze({ priceMap: Object.freeze({}) });

export const vendorPricingDocId = (vendorId, itemId) => `${vendorId}__${itemId}`;

export const isVendorPricingOn = (isModuleEnabled) =>
  typeof isModuleEnabled === 'function' && isModuleEnabled(VENDOR_PRICING_MODULE_ID) === true;

/** All price rows of one vendor → { priceMap: { itemId: price } }. */
export const loadVendorPricing = async (companyId, vendorId) => {
  if (!companyId || !vendorId) return EMPTY_VENDOR_PRICING;
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, VENDOR_PRICING_COLLECTION),
    where('vendorId', '==', vendorId)
  ));
  const priceMap = {};
  snap.docs.forEach(d => {
    const r = d.data();
    if (r?.itemId && Number(r.price) > 0) priceMap[r.itemId] = Number(r.price);
  });
  return { priceMap };
};

/**
 * The vendor's rate for an item in the line's unit, or null when it has none.
 * The list holds the price per BASE unit (like the item's purchase price), so
 * a line in a bigger unit (e.g. Box of 10) gets price × that unit's conversion.
 */
export const getVendorPrice = (pricing, itemId, item = null, unitCode = '') => {
  if (!pricing || !pricing.priceMap || !itemId) return null;
  const v = Number(pricing.priceMap[itemId]);
  if (!(v > 0)) return null;
  const f = item && unitCode ? conversionFactor(item, unitCode) : 1;
  return Math.round(v * f * 10000) / 10000;
};

/**
 * Purchase screens: the selected vendor's price list, reloaded whenever the
 * vendor changes. Stays empty (and reads nothing) while the module is off.
 */
export const useVendorPricing = (companyId, vendorId, isModuleEnabled) => {
  const on = isVendorPricingOn(isModuleEnabled);
  const [pricing, setPricing] = useState(EMPTY_VENDOR_PRICING);
  useEffect(() => {
    let alive = true;
    if (!on || !companyId || !vendorId) {
      setPricing(EMPTY_VENDOR_PRICING);
      return undefined;
    }
    loadVendorPricing(companyId, vendorId)
      .then(p => { if (alive) setPricing(p); })
      .catch(err => { console.error('Vendor pricing load failed:', err); if (alive) setPricing(EMPTY_VENDOR_PRICING); });
    return () => { alive = false; };
  }, [on, companyId, vendorId]);
  return pricing;
};
